// The Character.AI importer's pieces that are not screen (src/component/cai-import/Importer.jsx; backend
// myapp/cai_import.py, which decides everything that matters: what is refused, the free portrait, the limits).
// Pure functions are unit-tested in tests/cai-import.test.mjs; the image and storage helpers need a browser.

export const MAX_SHOTS = 5
// The create-character form's limits (CharacterInputs.jsx); the backend holds the same ones.
export const LIMITS = {
  name: 30,
  personality: 300,
  scenario: 300,
  first_message: 300,
  portrait_prompt: 400,
}

/** What to tell the person for a failed call to the importer's routes or to /api/generate. */
export function importErrorMessage(status, body = {}) {
  if (body?.error && [400, 401, 403, 422, 429, 503].includes(status)) {
    return body.reason ? `${body.error} (${body.reason})` : body.error
  }
  if (status === 404)
    return 'The importer is switched off right now. Please try again later.'
  if (status === 402) return 'Not enough Gems to generate another portrait.'
  if (status === 421)
    return 'You have used today’s free XL images. Upgrade or try again tomorrow.'
  if (status === 300)
    return 'That portrait prompt has words we can’t use. Please edit it and try again.'
  return 'Something went wrong. Please try again.'
}

/** The fields the create route takes, from the review form. */
export function createBody(fields, portrait) {
  const body = { portrait }
  for (const key of [
    'name',
    'personality',
    'scenario',
    'first_message',
    'appearance',
    'gender',
    'sexuality',
    'style',
    'portrait_prompt',
  ]) {
    body[key] = typeof fields[key] === 'string' ? fields[key] : ''
  }
  body.age = Number(fields.age) || 21
  return body
}

/** The ImageSettings (generate/requests.ts imageRequest) for one portrait with the settings the backend chose. */
export function portraitSettings({ portrait, prompt, plan, seed, jobId }) {
  return {
    model: portrait.model,
    prompt,
    negativePrompt: '',
    seed,
    quality: portrait.quality,
    creativity: 50,
    imageSize: portrait.imageSize,
    ntier: 1,
    lora: 'Default',
    lorasList: [],
    filter: 'Default',
    details: 0,
    emotion: 'Default',
    faceFix: false,
    chillMode: false,
    safeImage: false,
    watermark: false,
    plan,
    language: 'en',
    animation: null,
    stickMode: false,
    folderSetting: null,
    characterVersion: 'v3',
    charImage: null,
    charImageIds: null,
    customPoseImage: null,
    showAttributes: false,
    age: null,
    weight: null,
    breasts: null,
    ass: null,
    penis: null,
    females: null,
    males: null,
    nsfwEnabled: false,
    visibilityMode: null,
    jobId,
  }
}

// Browser only below.

/**
 * A screenshot shrunk to at most 1600px on its long side, as JPEG: small enough to send and to keep while a guest
 * signs up, and still readable (the backend's reader works at about that size anyway).
 */
export async function shrinkScreenshot(file) {
  const bitmap = await createImageBitmap(file)
  const scale = Math.min(1, 1600 / Math.max(bitmap.width, bitmap.height))
  const canvas = document.createElement('canvas')
  canvas.width = Math.round(bitmap.width * scale)
  canvas.height = Math.round(bitmap.height * scale)
  canvas.getContext('2d').drawImage(bitmap, 0, 0, canvas.width, canvas.height)
  bitmap.close?.()
  return new Promise((resolve, reject) =>
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('toBlob'))),
      'image/jpeg',
      0.85,
    ),
  )
}

export function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(String(reader.result).split(',', 2)[1])
    reader.onerror = () => reject(reader.error)
    reader.readAsDataURL(blob)
  })
}

// A guest's screenshots wait here while they make an account (IndexedDB: a few hundred KB of images is past what
// iOS lets localStorage hold). Every call is safe anywhere: no IndexedDB, or one that throws (private Safari), just
// means nothing is kept, and the person picks their screenshots again.
const DB = 'cai-import'
const STORE = 'shots'
const PENDING = 'pending'
const PENDING_TTL_MS = 24 * 60 * 60 * 1000

function withStore(mode, run) {
  return new Promise((resolve) => {
    try {
      const open = indexedDB.open(DB, 1)
      open.onupgradeneeded = () => open.result.createObjectStore(STORE)
      open.onerror = () => resolve(null)
      open.onsuccess = () => {
        const db = open.result
        try {
          const tx = db.transaction(STORE, mode)
          const request = run(tx.objectStore(STORE))
          tx.oncomplete = () => {
            db.close()
            resolve(request?.result ?? null)
          }
          tx.onerror = tx.onabort = () => {
            db.close()
            resolve(null)
          }
        } catch {
          db.close()
          resolve(null)
        }
      }
    } catch {
      resolve(null)
    }
  })
}

export const savePendingShots = (blobs) =>
  withStore('readwrite', (store) =>
    store.put({ blobs, at: Date.now() }, PENDING),
  )

export async function loadPendingShots() {
  const saved = await withStore('readonly', (store) => store.get(PENDING))
  if (!saved || Date.now() - saved.at > PENDING_TTL_MS) return []
  return Array.isArray(saved.blobs) ? saved.blobs : []
}

export const clearPendingShots = () =>
  withStore('readwrite', (store) => store.delete(PENDING))
